// frontend/src/components/MentionTextarea.js

import React, { useMemo, useRef, useState, useLayoutEffect } from "react";
import { api } from "api";
import { DEFAULT_CIRCLE_IMAGE, DEFAULT_PROFILE_IMAGE } from "../utils/blank-image";

// Canonical mention token: @[Display Name](u:123)
export const MENTION_RE = /@\[(.+?)\]\(u:(\d+)\)/g;

const escapeHtml = (s = "") =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// Build the underlay HTML with bold/blue mentions.
// When empty, we render the placeholder (escaped) instead.
const buildHighlightedHtml = (value = "", placeholder = "", people = []) => {
  const text = String(value);
  if (!text) return escapeHtml(placeholder);

  let html = "";
  let i = 0;

  const labels = people
    .map((p) => p.label)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);

  while (i < text.length) {
    let matched = null;

    for (const label of labels) {
      const token = `@${label}`;

      if (
        text.slice(i, i + token.length) === token &&
        (i === 0 || /\s/.test(text[i - 1])) &&
        (i + token.length === text.length || /[\s.,!?;:)\]]/.test(text[i + token.length]))
      ) {
        matched = token;
        break;
      }
    }

    if (matched) {
      html += `<span class="font-medium text-blue-800">${escapeHtml(matched)}</span>`;
      i += matched.length;
    } else {
      const handleMatch = text.slice(i).match(/^@[A-Za-z0-9_.]+/);

      if (
        handleMatch &&
        (i === 0 || /\s/.test(text[i - 1])) &&
        (i + handleMatch[0].length === text.length ||
          /[\s.,!?;:)\]]/.test(text[i + handleMatch[0].length]))
      ) {
        html += `<span class="font-medium text-blue-800">${escapeHtml(
          handleMatch[0]
        )}</span>`;
        i += handleMatch[0].length;
      } else {
        html += escapeHtml(text[i]);
        i += 1;
      }
    }
  }

  return html.replace(/\n/g, "<br/>");
};

export default function MentionTextarea({
  id,
  value,
  onChange,
  placeholder = "Type here…",
  className = "",
  textareaClassName = "",
  rows = 3,
}) {
  const taRef = useRef(null);

  // following-only list to suggest from
  const [people, setPeople] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const loadedRef = useRef(false);

  const autosize = () => {
    const el = taRef.current;
    if (!el) return;
    // reset to let it shrink if needed, then grow to content
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  useLayoutEffect(() => {
    autosize();
  }, [value, rows]);  

  // Load "following" once on focus
  const loadFollowing = async () => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    try {
      const token = localStorage.getItem("token");
      const meId = localStorage.getItem("user_id");
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
      const url = meId
        ? `/connections/following/${meId}`
        : "/connections/following/me";
      const { data } = await api.get(url, { headers });
      const list = Array.isArray(data) ? data : [];
      setPeople(
        list.map((u) => ({
          id: u.id,
          label:
            u.name ||
            u.display_name ||
            u.username ||
            (u.id ? `User #${u.id}` : "User"),
          handle: u.username || "",
          avatar: u.profile_pic_url || DEFAULT_PROFILE_IMAGE,
        }))
      );
    } catch {
      setPeople([]);
    }
  };

  // Find an "@query" at the caret
  const computeQuery = (nextValue = value) => {
    const el = taRef.current;
    if (!el) return { found: false, start: 0, end: 0, text: "" };

    const caret = el.selectionStart ?? 0;
    const pre = String(nextValue).slice(0, caret);

    const m = pre.match(/(^|\s)@([^\s@]{0,30})$/);
    if (!m) return { found: false, start: 0, end: caret, text: "" };

    const start = caret - (m[2]?.length || 0) - 1;
    return { found: true, start, end: caret, text: m[2] || "" };
  };

  const suggestions = useMemo(() => {
    if (!open) return [];
    const needle = q.toLowerCase();
    return people
      .filter(
        (p) =>
          p.label.toLowerCase().includes(needle) ||
          (p.handle && p.handle.toLowerCase().includes(needle))
      )
      .slice(0, 8);
  }, [open, q, people]);

  const insertMention = (person) => {
    const el = taRef.current;
    if (!el) return;
    const { found, start, end } = computeQuery();
    if (!found) return;

    const before = String(value).slice(0, start);
    const after = String(value).slice(end);
    // Insert token (rendering to links happens later in card)
    const token = `@${person.label}`;
    const next = `${before}${token}${after}`;

    onChange?.(next);

    // Place caret right after the inserted token
    requestAnimationFrame(() => {
      el.focus();
      const pos = before.length + token.length;
      el.setSelectionRange(pos, pos);
    });

    setOpen(false);
    setActive(0);
    setQ("");
  };

  const onKeyDown = (e) => {
    if (!open || !suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      insertMention(suggestions[active]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  const onInput = (e) => {
    const val = e.target.value;
    onChange?.(val);
    autosize();

    const { found, text } = computeQuery(val);
    setOpen(found);
    setQ(text);
  };
  
  const highlightHtml = useMemo(
    () => buildHighlightedHtml(value, placeholder, people),
    [value, placeholder, people]
  );

  return (
    <div className={`relative ${className}`} onKeyDown={onKeyDown}>
      <div
        className={`
          relative rounded border border-gray-200 px-3 py-0 bg-white rounded-2xl text-sm
          focus-within:border-blue-600 focus-within:ring-1 focus-within:ring-blue-600
        `}
      >
        {/* Underlay */}
        <div
          aria-hidden
          className={`absolute inset-0 rounded-2xl overflow-hidden pointer-events-none px-3 py-2 whitespace-pre-wrap break-words leading-[1.4]
            ${value ? "text-black" : "text-gray-400"}`}
          style={{ font: "inherit" }}
          dangerouslySetInnerHTML={{ __html: highlightHtml }}
        />

        <textarea
          id={id}
          ref={taRef}
          rows={rows}
          value={value}
          aria-label={placeholder}
          onChange={onInput}
          onFocus={() => {
            loadFollowing();
            autosize();
          }}
          className={`
            relative block w-full bg-transparent border-0 outline-none
            rounded-2xl py-2 px-0 m-0 caret-black text-transparent font-medium
            leading-[1.4] overflow-hidden
            ${textareaClassName}
          `}
          style={{
            resize: "none",
            font: "inherit",
          }}
        />
      </div>

      {/* Suggestions */}
      {open && suggestions.length > 0 && (
        <div className="relative z-50 mt-1 max-h-64 overflow-auto rounded-2xl border border-blue-100 bg-white shadow-lg">
          {suggestions.map((p, i) => (
            <button
              key={p.id}
              type="button"
              onMouseDown={(e) => {
                // prevent blur so click inserts properly
                e.preventDefault();
                insertMention(p);
              }}
              className={`flex w-full items-center gap-2 px-3 py-1.5 text-left ${
                i === active ? "bg-blue-50" : ""
              }`}
            >
              <img
                src={p.avatar}
                alt={p.label}
                className="h-6 w-6 rounded-full object-cover"
              />
              <div className="min-w-0">
                <div className="truncate text-[0.95rem] font-medium">
                  {p.label}
                </div>
                {p.handle && (
                  <div className="truncate !min-h-0 !min-w-0 text-xs text-gray-500">@{p.handle}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
